import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { email } from '@certemy/common';
import _get from 'lodash-es/get';
import { LOGIN_ENTITY_NAME } from 'pages/entry/login/login.component.constants';
import { EntryService } from 'services/entry/entry.service';
import { Store } from '@ngrx/store';
import { Selectors as LoginProviderSelectors } from 'services/login-provider/+state/login-provider.state';
import { map } from 'rxjs/operators';
import { ExternalLoginSettings } from 'interfaces/external-login.interface';
import { SIGN_IN_TYPES } from 'pages/entry/entry.helpers';

@Component({
  selector: 'cm-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
})
export class LoginFormComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  private _isSubmitted = false;
  private _errorMessage: string;
  private onDestroy$ = new Subject<void>();
  private _loginType: SIGN_IN_TYPES = SIGN_IN_TYPES.DEFAULT;

  @ViewChild('email')
  private usernameInput: ElementRef<any>;
  @ViewChild('password')
  private passwordInput: ElementRef<any>;

  @Input()
  set loginType(loginType: SIGN_IN_TYPES) {
    this._loginType = loginType;
    const emailValue = this.form.get('username').value || '';
    this.form.removeControl('username');
    this.form.addControl(
      'username',
      new UntypedFormControl(emailValue, Validators.compose([Validators.required, ...(this.defaultLoginType ? [email] : [])])),
    );
  }

  get loginType(): SIGN_IN_TYPES {
    return this._loginType;
  }

  @Input()
  set isSubmitted(submitted) {
    this._isSubmitted = submitted;
  }
  get isSubmitted(): boolean {
    return this._isSubmitted;
  }

  @Input()
  set errorMessage(error) {
    this._errorMessage = error;
  }
  get errorMessage(): string {
    return this._errorMessage;
  }
  @Output() submitForm = new EventEmitter();
  @Output() tokenLogin = new EventEmitter();

  get defaultLoginType(): boolean {
    return this._loginType === SIGN_IN_TYPES.DEFAULT;
  }

  constructor(private entryService: EntryService, private ngZone: NgZone, private store: Store<any>) {
    const msgType = entryService.getMessageType();
    let username = '';
    if (msgType.login && msgType.email) {
      username = msgType.email;
    }
    this.form = new UntypedFormGroup({
      username: new UntypedFormControl(username, Validators.compose([Validators.required, email])),
      password: new UntypedFormControl('', Validators.compose([Validators.required])),
    });
  }

  @Input()
  settings$: Observable<ExternalLoginSettings> = this.store
    .select(LoginProviderSelectors.loginProviderMappedState)
    .pipe(
      map((data) => ({
        ...data,
        url: window.location.href,
      })),
    );

  ngOnInit() {}

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  handleCredentialResponse(response) {
    this.ngZone.run(() => {
      this.tokenLogin.emit(response);
    });
  }

  getFormData() {
    if (this.form.valid) {
      return this.form.getRawValue();
    } else if (this.wasAutoCompleted()) {
      return this.getFormInputsValue();
    } else {
      this.errorMessage = this.getFormError();
      return null;
    }
  }

  private wasAutoCompleted(): boolean {
    const nativeValue = this.getFormInputsValue();
    return Object.entries(this.form.controls).some(([name, control]) => control.pristine && nativeValue[name]);
  }

  private getFormError(): string {
    const { password, username } = this.form.controls;
    if (_get(password.errors, 'required') || _get(username.errors, 'required')) {
      return 'Please fill out all fields';
    } else if (username.errors) {
      return `Please enter the valid ${this.defaultLoginType ? LOGIN_ENTITY_NAME.EMAIL : LOGIN_ENTITY_NAME.USERNAME}`;
    } else {
      return '';
    }
  }

  private getFormInputsValue() {
    return { username: this.usernameInput.nativeElement.value, password: this.passwordInput.nativeElement.value };
  }

  disableForm() {
    this.form.disable();
  }

  onSubmit() {
    this.submitForm.emit();
  }
}
